import { useAtomValue } from 'jotai';
import { useCallback } from 'react';
import { useParams } from 'react-router';
import type { BreadcrumbItem } from '~/components/breadcrumbs';
import type { NavigationItem } from '~/components/granular';
import { userConfig } from '~/global-state/userConfig';
import { useNavigation } from '~/hooks';

export function useBreadcrumbs() {
  const params = useParams();

  const { isEndConsumer } = useAtomValue(userConfig);
  const { navigation, footerNavigation } = useNavigation(isEndConsumer);

  const getBreadcrumbs = useCallback(
    (path: string) => {
      const getBreadcrumbs = (links: NavigationItem[]): BreadcrumbItem[] => {
        for (const link of links) {
          if (!link.to && !link.breadcrumbRoutes) {
            continue;
          }

          if (link.breadcrumbRoutes) {
            // matches routes with dynamic id param
            for (const linkTo of link.breadcrumbRoutes) {
              if (path.startsWith(linkTo)) {
                return [{ label: link.label, to: linkTo }, ...getBreadcrumbs(link.links ?? [])];
              }
            }

            continue;
          }

          if (link.to) {
            // matches routes with dynamic id param
            const linkTo = typeof link.to === 'function' ? link.to(params.id ?? '') : link.to;
            const [linkToRoot] = linkTo.split('/custom-views');

            if (path.startsWith(linkToRoot)) {
              return [{ label: link.label, to: linkTo }, ...getBreadcrumbs(link.links ?? [])];
            }
          }
        }

        return [];
      };

      return getBreadcrumbs([...navigation, ...footerNavigation].flatMap(({ links }) => links));
    },
    [footerNavigation, navigation, params],
  );

  return { getBreadcrumbs };
}
